/* ============================================================
   section-image-parallax.js
   Vertical card-stacking / peel deck for the homepage image section.
   Powered by assets/card-deck.js (no third-party dependencies).
   ============================================================ */

/* ------------------------------------------------------------------
   Header ownership constants.

   The value we park in data-header-sticky while the deck owns the header.
   theme.js's Sticky controller derives everything it does from that one
   attribute in its constructor -- `sticks = value === 'sticky'`,
   `animated = value === 'directional'` -- so a third value leaves both false
   and its listen() a no-op. That is the whole stand-down: no theme.js code
   has to be disabled or duplicated, and restoring the original value restores
   the theme's own behaviour exactly. Nothing else in the theme reads this
   attribute except two `[data-header-sticky="sticky"]` probes, which were
   already false in this theme's 'directional' mode.
   ------------------------------------------------------------------ */
const HEADER_STICKY_DECK = 'deck';
const HEADER_OWNED_FLAG = 'deck-owns-header';

/* The legacy header classes, which the deck is now the only writer of.
   `stuck` lifts the header out of flow (position: fixed);
   `animated` arms the transform transition and is what makes it hideable;
   `shown` is the show/hide switch;
   `backdrop` is the blur, deliberately never applied -- see _claimHeader. */
const HEADER_CLASSES = {
  stuck: 'js__header__stuck',
  animated: 'js__header__stuck--animated',
  shown: 'js__header__stuck--trigger-animation',
  backdrop: 'js__header__stuck__backdrop'
};

class ImageParallax extends HTMLElement {
  constructor() {
    super();
    this.deck = null;
  }

  connectedCallback() {
    this._initDeck();
    this._initAccordions();
    this._initBackButton();
  }

  _initBackButton() {
    const backBtn = this.querySelector('[data-back-button]');
    if (!backBtn) return;
    this._onBackClick = (e) => {
      if (document.referrer && document.referrer.indexOf(window.location.host) !== -1) {
        e.preventDefault();
        window.history.back();
      }
    };
    backBtn.addEventListener('click', this._onBackClick);
  }

  _initDeck() {
    const deckEl = this.querySelector('[data-deck]');
    if (!deckEl) return;

    // Merchant can switch the card-peel transition off in the theme editor,
    // in which case the slides degrade to plain stacked full-height sections.
    if (this.dataset.enableSnap === 'false') {
      deckEl.classList.add('sip-deck--native');
      return;
    }

    const start = () => {
      if (typeof window.CardDeck === 'undefined') {
        // card-deck.js is deferred too; wait for it rather than racing it.
        requestAnimationFrame(start);
        return;
      }
      this.deck = new window.CardDeck(deckEl);
      this._markScrollableSlides();
      this._initVideoGating();
      this._initHeaderOwnership();
      this._initAutoplay();
      this._initBackgroundPreloader();
    };

    start();
  }

  /**
   * Background image preloader.
   * After the initial hero image loads, sequentially pre-fetches upcoming parallax
   * slide images into the browser cache during idle frames so subsequent cards
   * decode instantaneously without blank flashes when the user scrolls.
   */
  _initBackgroundPreloader() {
    const warmNextImages = () => {
      if (!this.deck || this.deck.destroyed) return;
      const lazyImgs = Array.prototype.slice.call(this.querySelectorAll('.sip-deck img[loading="lazy"]'));
      if (!lazyImgs.length) return;

      let idx = 0;
      const loadNext = () => {
        if (idx >= lazyImgs.length) return;
        const img = lazyImgs[idx++];
        if (img.dataset.preloaded) {
          loadNext();
          return;
        }
        img.dataset.preloaded = 'true';
        const dummy = new Image();
        dummy.decoding = 'async';
        if (img.srcset) dummy.srcset = img.srcset;
        if (img.sizes) dummy.sizes = img.sizes;
        dummy.src = img.src || img.currentSrc;
        dummy.onload = dummy.onerror = () => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(loadNext, { timeout: 800 });
          } else {
            setTimeout(loadNext, 120);
          }
        };
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(loadNext, { timeout: 1200 });
      } else {
        setTimeout(loadNext, 300);
      }
    };

    const firstImg = this.querySelector('.sip-deck img[loading="eager"]') || this.querySelector('.sip-deck img');
    if (firstImg && !firstImg.complete) {
      firstImg.addEventListener('load', () => setTimeout(warmNextImages, 800), { once: true });
    } else {
      setTimeout(warmNextImages, 1200);
    }
  }

  /**
   * Automatic slide progression at configurable intervals.
   * Pauses on user interaction, off-screen visibility, or when reduced motion is preferred.
   */
  _initAutoplay() {
    if (!this.deck) return;
    if (this.dataset.enableAutoplay !== 'true') return;

    const intervalSec = parseFloat(this.dataset.autoplayInterval) || 5;
    this._autoplayDelay = Math.max(1, Math.min(10, intervalSec)) * 1000;
    this._autoplayTimer = null;
    this._autoplayIntersecting = false;

    this._startAutoplay = () => {
      this._stopAutoplay();
      if (!this._autoplayIntersecting) return;
      this._autoplayTimer = setInterval(() => {
        if (!this.deck || this.deck.destroyed || this.deck.locked) return;
        if (this.deck.animating || (this.deck._drag && this.deck._drag.decided)) return;
        if (!this._autoplayIntersecting) return;

        if (this.deck.isEnd()) {
          this.deck.goTo(0);
        } else {
          this.deck.next();
        }
      }, this._autoplayDelay);
    };

    this._stopAutoplay = () => {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    };

    this._resetAutoplay = () => {
      if (this.dataset.enableAutoplay !== 'true') return;
      this._startAutoplay();
    };

    // User activity listener: restart the countdown so manual navigation gets full interval
    this._onDeckUserActivity = () => {
      this._resetAutoplay();
    };

    this.deck.root.addEventListener('deck:change', this._onDeckUserActivity);
    this.deck.root.addEventListener('deck:dragstart', this._onDeckUserActivity);
    this.addEventListener('touchstart', this._onDeckUserActivity, { passive: true });
    this.addEventListener('wheel', this._onDeckUserActivity, { passive: true });

    // IntersectionObserver ensures autoplay only runs when the section is on screen
    if ('IntersectionObserver' in window) {
      this._autoplayObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this._autoplayIntersecting = entry.isIntersecting && entry.intersectionRatio >= 0.4;
          if (this._autoplayIntersecting) {
            this._startAutoplay();
          } else {
            this._stopAutoplay();
          }
        });
      }, { threshold: [0, 0.4, 1.0] });
      this._autoplayObserver.observe(this);
    } else {
      this._autoplayIntersecting = true;
      this._startAutoplay();
    }
  }

  /**
   * Keep at most three video decoders alive: the card on screen and its two
   * immediate neighbours, which are the only cards reachable from here in
   * either direction.
   *
   * Slide videos are `autoplay loop`, so without this every video slide decodes
   * continuously for the whole visit — including while buried under other cards
   * where nobody can see it. That is main-thread and battery cost spent on
   * nothing, and it competes with the card transition for exactly the frames
   * the transition needs. Pre-rolling both neighbours means playback is already
   * running before any of the card is on screen, so nothing about the look
   * changes in either direction.
   */
  _initVideoGating() {
    if (!this.deck) return;

    const videos = this.deck.slides.map((slide) => slide.querySelectorAll('video'));
    if (!videos.some((list) => list.length)) return;

    const apply = () => {
      const active = this.deck.index;
      videos.forEach((list, i) => {
        // Neighbours, not just the next one: a backward drag reveals the card
        // BELOW the active one, and it must already be playing.
        const shouldPlay = Math.abs(i - active) <= 1;
        list.forEach((video) => {
          if (shouldPlay) {
            if (video.paused) {
              // Autoplay can reject (e.g. an un-muted video); nothing to do
              // about it here, and an unhandled rejection would be noise.
              const p = video.play();
              if (p && p.catch) p.catch(() => {});
            }
          } else if (!video.paused) {
            // Clearing autoplay matters as much as the pause. The tags are
            // rendered with autoplay:true, and autoplay fires whenever the
            // element becomes ready — which for a distant slide is usually
            // AFTER this ran, so a bare pause() would simply be undone.
            // Neighbours keep their autoplay attribute so they can still start
            // on their own if a programmatic play() is ever refused.
            video.autoplay = false;
            video.pause();
          } else {
            video.autoplay = false;
          }
        });
      });
    };

    this._onDeckVideoSync = apply;
    this._videoDeckEl = this.deck.root;
    // dragstart fires as the finger starts moving a card, not when it lands, so
    // the incoming video is rolling well before any of it is visible.
    this._videoDeckEl.addEventListener('deck:change', apply);
    this._videoDeckEl.addEventListener('deck:dragstart', apply);
    apply();
  }

  /**
   * A slide only gets internal scrolling if its content genuinely overflows.
   * Marked slides keep their own scroll; the deck advances once that inner
   * scroll reaches its edge.
   */
  _markScrollableSlides() {
    if (!this.deck) return;
    this.deck.slides.forEach((slide) => {
      if (!slide.hasAttribute('data-deck-scrollable')) return;
      const overflows = slide.scrollHeight > slide.clientHeight + 4;
      slide.classList.toggle('deck__slide--scrollable', overflows);
    });
  }

  _initAccordions() {
    this._onAccordionClick = (e) => {
      if (window.innerWidth >= 768) return;
      const trigger = e.target.closest('[data-sip-accordion-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const col = trigger.closest('.sip__footer-col');
      if (!col) return;
      const body = col.querySelector('[data-sip-accordion-body]');
      if (!body) return;

      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !isExpanded);
      trigger.classList.toggle('is-open', !isExpanded);
      body.classList.toggle('is-open', !isExpanded);

      // Opening an accordion changes the footer slide's height, which can
      // flip it into or out of needing its own scroll.
      if (this.deck) {
        this.deck.update();
        setTimeout(() => this._markScrollableSlides(), 380);
      }
    };
    this.addEventListener('click', this._onAccordionClick);

    this._onResize = () => this._markScrollableSlides();
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  /**
   * Take sole ownership of the header.
   *
   * WHY THIS IS OWNERSHIP AND NOT SYNC
   * ----------------------------------
   * The deck replaces the page's vertical scroll with a discrete index, so
   * window.scrollY stops describing where the visitor is -- it sits at 0 for the
   * entire visit. theme.js's Sticky controller (directional mode) decides header
   * visibility from window.scrollY, so on this page it permanently concludes
   * "we are at the very top" and clears the shown-class.
   *
   * The previous implementation added the shown-class and then dispatched a
   * synthetic `theme:scroll:up`. Dispatch is synchronous, so Sticky ran on the
   * very next line and removed the class again, leaving only `animated`, which
   * parks the header at translateY(-100%). Every backward move lost the header,
   * which is why it appeared to vanish for good after the first card.
   *
   * A deck index is not a scroll offset, and broadcasting it as one is what
   * created the conflict. So the deck stops pretending to scroll and instead
   * becomes the single writer:
   *
   *   1. Sticky is stood down declaratively (see HEADER_STICKY_DECK). Safe to do
   *      from here: theme.js is deferred and builds Sticky on DOMContentLoaded,
   *      while this runs during customElements.define, which is earlier.
   *   2. The overlay state is applied ONCE, in _claimHeader. Applying it on the
   *      first deck event instead -- as before -- is what made the deck jump up
   *      by the header's height the first time the visitor moved.
   *   3. From then on exactly ONE class is toggled, through one setter. One
   *      writer, one class, one source of truth: the deck's own index.
   */
  _initHeaderOwnership() {
    if (!this.deck) return;
    if (!this._ownsHeader()) return;

    this._headerVisible = null;
    this._headerVisibleBeforeDrag = null;

    if (!this._claimHeader(document.querySelector('[data-header-wrapper]'))) return;

    // The whole visibility rule, in one place.
    const stateFor = (index, previousIndex) => {
      // The first card is the top of the page. The header belongs on screen
      // there, whichever direction brought us back to it.
      if (index === 0) return true;
      // Otherwise plain directional behaviour: forward hides, backward reveals.
      return index < previousIndex;
    };

    this._onDeckHeaderChange = (e) => {
      this._setHeaderVisible(stateFor(e.detail.index, e.detail.previousIndex));
    };

    // The card is already travelling under the finger, so the header should be
    // getting out of the way before the card lands rather than after.
    this._onDeckHeaderDragStart = (e) => {
      this._headerVisibleBeforeDrag = this._headerVisible;
      this._setHeaderVisible(stateFor(e.detail.targetIndex, e.detail.index));
    };

    // A drag that did not travel far enough springs the card back, and no
    // deck:change follows it -- so the prediction made at dragstart has to be
    // undone here or the header stays in a state for a move that never happened.
    // A committed drag is left alone: _commitIndex fires deck:change before
    // dragend, so it has already been reconciled.
    this._onDeckHeaderDragEnd = (e) => {
      if (e.detail.committed) return;
      this._setHeaderVisible(this._headerVisibleBeforeDrag);
    };

    this.deck.root.addEventListener('deck:change', this._onDeckHeaderChange);
    this.deck.root.addEventListener('deck:dragstart', this._onDeckHeaderDragStart);
    this.deck.root.addEventListener('deck:dragend', this._onDeckHeaderDragEnd);

    // The theme editor replaces the entire header section element when its
    // settings change, which takes our ownership marks -- and our element
    // reference -- with it.
    this._onHeaderSectionLoad = (e) => {
      const el = e.target;
      if (!el || typeof el.matches !== 'function') return;
      if (!el.matches('[data-header-wrapper]')) return;
      this._headerVisible = null;
      this._claimHeader(el);
    };
    document.addEventListener('shopify:section:load', this._onHeaderSectionLoad);
  }

  /**
   * The deck only owns the header when it is the page's FIRST section -- the
   * arrangement where it replaces the page's scroll and the header is meant to
   * float over it. Placed further down a page the deck is one section among
   * several, the page scrolls for real, and the theme's own Sticky controller is
   * the correct owner, so leave it alone.
   *
   * section-image-parallax.css gates the backfill rule on the same condition, so
   * the CSS and the JS can never disagree about who is in charge.
   */
  _ownsHeader() {
    const section = this.closest('.shopify-section');
    return !!section && section.matches('.main-content > .shopify-section:first-child');
  }

  /**
   * Mark the header deck-owned and put it in its resting overlay state.
   * Returns false when the page has no header at all (the immersive layout
   * renders none), in which case there is nothing to own.
   */
  _claimHeader(wrapper) {
    if (!wrapper) return false;
    this._headerWrapper = wrapper;

    // Preserve whatever the merchant configured so _releaseHeader can hand the
    // header back untouched. Guarded so a re-claim cannot overwrite the real
    // value with our own sentinel.
    if (wrapper.dataset.headerSticky !== HEADER_STICKY_DECK) {
      wrapper.dataset.headerStickyBase = wrapper.dataset.headerSticky || '';
    }
    wrapper.dataset.headerSticky = HEADER_STICKY_DECK;
    document.documentElement.classList.add(HEADER_OWNED_FLAG);

    // Resting overlay state, applied once. The backdrop blur is deliberately
    // cleared and never re-added: the header now sits over slide content at all
    // times, and the nav's solid background is what keeps it legible over both
    // the photographic slides and the white collection-grid slide.
    wrapper.classList.add(HEADER_CLASSES.stuck, HEADER_CLASSES.animated);
    wrapper.classList.remove(HEADER_CLASSES.backdrop);

    // Same rule as stateFor(): resting on the first card means shown.
    this._setHeaderVisible(this.deck ? this.deck.index === 0 : true);
    return true;
  }

  /**
   * The single writer. Both the drag path and the settled-change path come
   * through here, so they cannot end up disagreeing, and the class is only
   * touched when the state actually changes.
   */
  _setHeaderVisible(visible) {
    if (!this._headerWrapper) return;
    visible = !!visible;
    if (this._headerVisible === visible) return;
    this._headerVisible = visible;
    this._headerWrapper.classList.toggle(HEADER_CLASSES.shown, visible);
  }

  /** Hand the header back exactly as we found it. */
  _releaseHeader() {
    if (this._onHeaderSectionLoad) {
      document.removeEventListener('shopify:section:load', this._onHeaderSectionLoad);
      this._onHeaderSectionLoad = null;
    }
    if (this.deck && this._onDeckHeaderChange) {
      this.deck.root.removeEventListener('deck:change', this._onDeckHeaderChange);
      this.deck.root.removeEventListener('deck:dragstart', this._onDeckHeaderDragStart);
      this.deck.root.removeEventListener('deck:dragend', this._onDeckHeaderDragEnd);
    }
    this._onDeckHeaderChange = null;
    this._onDeckHeaderDragStart = null;
    this._onDeckHeaderDragEnd = null;

    const wrapper = this._headerWrapper;
    this._headerWrapper = null;
    this._headerVisible = null;
    if (!wrapper) return;

    wrapper.classList.remove(HEADER_CLASSES.stuck, HEADER_CLASSES.animated, HEADER_CLASSES.shown);
    if (typeof wrapper.dataset.headerStickyBase === 'string') {
      wrapper.dataset.headerSticky = wrapper.dataset.headerStickyBase;
      delete wrapper.dataset.headerStickyBase;
    }
    // Only surrender the document flag once no other deck is left holding the
    // header. `this` is already detached by the time disconnectedCallback runs,
    // so this query cannot match us.
    if (!document.querySelector('image-parallax')) {
      document.documentElement.classList.remove(HEADER_OWNED_FLAG);
    }
  }

  disconnectedCallback() {
    if (this._onAccordionClick) {
      this.removeEventListener('click', this._onAccordionClick);
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    // These were attached directly rather than through the deck's own binder,
    // so destroy() will not sweep them up.
    if (this._videoDeckEl && this._onDeckVideoSync) {
      this._videoDeckEl.removeEventListener('deck:change', this._onDeckVideoSync);
      this._videoDeckEl.removeEventListener('deck:dragstart', this._onDeckVideoSync);
      this._videoDeckEl = null;
      this._onDeckVideoSync = null;
    }
    // Autoplay cleanup
    if (this._stopAutoplay) {
      this._stopAutoplay();
    }
    if (this._autoplayObserver) {
      this._autoplayObserver.disconnect();
      this._autoplayObserver = null;
    }
    if (this.deck && this._onDeckUserActivity) {
      this.deck.root.removeEventListener('deck:change', this._onDeckUserActivity);
      this.deck.root.removeEventListener('deck:dragstart', this._onDeckUserActivity);
      this.removeEventListener('touchstart', this._onDeckUserActivity);
      this.removeEventListener('wheel', this._onDeckUserActivity);
      this._onDeckUserActivity = null;
    }

    // Before destroy(), which drops the deck reference the listeners hang off.
    this._releaseHeader();
    if (this.deck) {
      this.deck.destroy();
      this.deck = null;
    }
  }
}

if (!customElements.get('image-parallax')) {
  customElements.define('image-parallax', ImageParallax);
}
